import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Play, Info, Star, Calendar, Clock } from 'lucide-react'
import { Anime } from '@/types/anime'

interface HeroProps {
  featuredAnime?: Anime
}

export function Hero({ featuredAnime }: HeroProps) {
  const [imageLoaded, setImageLoaded] = useState(false)

  if (!featuredAnime) {
    return (
      <div className="relative h-[70vh] bg-gradient-to-r from-dark-300 to-dark-200 flex items-center justify-center">
        <div className="animate-pulse">
          <div className="w-64 h-8 bg-gray-700 rounded mb-4"></div>
          <div className="w-96 h-4 bg-gray-700 rounded mb-2"></div>
          <div className="w-80 h-4 bg-gray-700 rounded"></div>
        </div>
      </div>
    )
  }

  const cleanDescription = featuredAnime.description
    ?.replace(/<[^>]*>/g, '')
    ?.substring(0, 200) + '...'

  return (
    <div className="relative h-[70vh] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        {featuredAnime.bannerImage && (
          <img
            src={featuredAnime.bannerImage}
            alt={featuredAnime.title.romaji}
            className={`w-full h-full object-cover transition-opacity duration-700 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-400 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative h-full flex items-center">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl">
            {/* Title */}
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
              {featuredAnime.title.english || featuredAnime.title.romaji}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-300">
              {featuredAnime.averageScore && (
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  <span className="text-yellow-400 font-medium">
                    {featuredAnime.averageScore}%
                  </span>
                </div>
              )}
              {featuredAnime.seasonYear && (
                <div className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>{featuredAnime.seasonYear}</span>
                </div>
              )}
              {featuredAnime.episodes && (
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{featuredAnime.episodes} Episodes</span>
                </div>
              )}
              <div className="px-2 py-1 bg-blue-600 text-white rounded text-xs font-medium uppercase">
                {featuredAnime.format}
              </div>
            </div>

            {/* Genres */}
            <div className="flex flex-wrap gap-2 mb-6">
              {featuredAnime.genres.slice(0, 4).map((genre) => (
                <span
                  key={genre}
                  className="px-3 py-1 bg-gray-800/80 text-gray-300 rounded-full text-sm backdrop-blur-sm"
                >
                  {genre}
                </span>
              ))}
            </div>

            {/* Description */}
            {cleanDescription && (
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                {cleanDescription}
              </p>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={`/watch/${featuredAnime.id}?episode=1`}
                className="flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Play className="w-5 h-5" />
                <span>Watch Now</span>
              </Link>
              <Link
                to={`/anime/${featuredAnime.id}`}
                className="flex items-center justify-center space-x-2 bg-gray-800/80 hover:bg-gray-700/80 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-200 backdrop-blur-sm border border-gray-600 hover:border-gray-500"
              >
                <Info className="w-5 h-5" />
                <span>More Info</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
